// ScrollableTable.tsx
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
  Typography,
} from "@mui/material";
import { useState } from "react";

interface Column {
  label: string;
  align?: "left" | "center" | "right";
  key: string;
}

interface ScrollableTableProps {
  columns: Column[];
  countCategories: any[];
  maxHeight?: number;
  borderColor?: string;
  scrollColor?: string;
}

export function ScrollableTable({
  columns,
  countCategories,
  maxHeight = 600,
  borderColor = "#000000ff",
  scrollColor = "#000000ff",
}: ScrollableTableProps) {
  const [ignore, setIgnore] = useState({
    category: false,
    gender: false,
    belt: false,
    weight: false,
  });

  const toggleIgnore = (key: keyof typeof ignore) => {
    setIgnore((prev: any) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Função auxiliar: gera o “título modificado” com base nos campos ignorados
  const getCategoryKey = (title: string) => {
    const [cat, gen, belt, weight] = title.split("/");

    return [
      ignore.category ? "x" : cat,
      ignore.gender ? "x" : gen,
      ignore.belt ? "x" : belt,
      ignore.weight ? "x" : weight,
    ].join("/");
  };

  // Reagrupar conforme os campos ignorados
 const groupedCategories = Object.values(
  countCategories.reduce((acc: any, item: any) => {
    const key = getCategoryKey(item.title);

    if (!acc[key]) {
      acc[key] = {
        title: key,
        total: 0,
        lutas: 0,
        tempoDeLuta: item.tempoDeLuta, // << pega do item
        tempoTotal: 0,
      };
    }

    acc[key].total += item.total;
    acc[key].lutas += item.lutas;
    acc[key].tempoTotal += item.tempoTotal;

    return acc;
  }, {})
);

  return (
    <>
      <Typography variant="h4" marginTop={5} fontWeight={"bold"}>
        Quantidade de atletas por categoria
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, mt: 2 }}>
        <Button
          variant={ignore.category ? "contained" : "outlined"}
          color="secondary"
          onClick={() => toggleIgnore("category")}
        >
          Ignorar Categoria
        </Button>
        <Button
          variant={ignore.gender ? "contained" : "outlined"}
          color="secondary"
          onClick={() => toggleIgnore("gender")}
        >
          Ignorar Gênero
        </Button>
        <Button
          variant={ignore.belt ? "contained" : "outlined"}
          color="secondary"
          onClick={() => toggleIgnore("belt")}
        >
          Ignorar Faixa
        </Button>
        <Button
          variant={ignore.weight ? "contained" : "outlined"}
          color="secondary"
          onClick={() => toggleIgnore("weight")}
        >
          Ignorar Peso
        </Button>

        <TableContainer
          sx={{
            maxHeight: maxHeight,
            overflowY: "auto",
            border: `1px solid ${borderColor}`,
            borderRadius: 3,
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: scrollColor,
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: scrollColor,
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#313131ff",
            },
            scrollbarWidth: "thin",
            scrollbarColor: `${scrollColor} transparent`,
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align ?? "left"}
                    sx={{
                      color: "white",
                      fontFamily: "inter, sans-serif",
                      backgroundColor: "#424242",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {groupedCategories.map((row: any, index: number) => (
                <TableRow key={index}>
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align ?? "left"}>
                      {typeof row[col.key] === "string"
                        ? row[col.key].toUpperCase()
                        : row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
}
